import { Suggestion } from './feature-app';

export interface OneFormState {
  formData: FormDataGroup;
  formInfo: FormInfo;
  dealersInfo: DealersInfo;
}

export interface Preconfiguration {
  activo: number;
  activoMatch: number;
  activoOffers: boolean;
  capacidadMaletero: number[];
  carlineName: string;
  carlineCode: number;
  carlineTitle: string;
  colores: {
    interior: string;
    exterior: {
      nombre: string;
      codigo: string;
      hexadecimal: string;
      invertido: number;
      atrevido: number;
      elegante: number;
    };
  };
  configToken: string;
  etiquetaDGT: string;
  conduccion: any;
  descripcion: string;
  descuentos: { [index: string]: number };
  destacados: any;
  equipamiento: string;
  id: number;
  marketingCode: string;
  llantas: string;
  marketingLongText: string;
  marketingShortText: string;
  espacio: any;
  motorizacion: string;
  pasajeros: any;
  plazas: number;
  precio: number;
  presupuesto: any;
  prioridad: number;
  tapiceria: string;
  titular: string;
  url: string;
  visible: boolean;
  title: string;
}
export interface RankedPreconfiguration extends Preconfiguration {
  operaciones?: any[];
  puntuacion?: number;
  puntuaciones?: number[];
  sumHighlights?: number;
  totalStyle?: number;
  affinity?: number;
  budgetOffset?: number;
}

export const isDealerData = (suggestion: Suggestion): suggestion is DealersData => {
  if (!suggestion) return null;
  return (suggestion as DealersData).dmsInfo !== undefined;
};
export interface DealersInfo {
  carlines: CarlineBase[];
  /**
   * Al momento de enviar una cita, necesitamos esta información para mapear correctamente el modelo, por lo que lo tenemos que cargar desde el principio para que esté disponible en el momento del envio.
   */
  citaCarlines: CarlineBase[];
  dealers: DealersData[];
  selectedCarline: CarlineBase;
  installations: Installation[];
  points: Points[];
  favouriteDealer: DealersData;
  /**
   *  Lo necesitamos en el caso de Cita Posventa Dealer, si el usuario tiene una concesión favorita la puede sobreescribir y debe tener prioridad la que viene por la url;
   */
  urlDealer: DealersData;
  selectedDealer: DealersData;
  selectedInstallation: Installation;
  dealersOrInstallationsMode: DealersOrInstallationsMode;
  oferta: Offer;
  salesGroup: SalesGroupInfo;
  match: RankedPreconfiguration;
}

export type DealersOrInstallationsMode = 'dealers' | 'installations';
export interface InstallationResponse {
  content: { attributes: DomainAttributesValues[] }[];
}

export interface FormDataGroup {
  fields: FormDataFields;
  headers: FormDataHeaders;
  flags?: FormDataFlags;
}

export type Horario = 'before' | 'morning' | 'evening' | 'afternon';
export interface FormDataFields {
  name?: string;
  horario?: Horario;
  slot?: Slot;
  [key: string]: any;
  dealer?: any;
}

export interface FormDataHeaders {
  [key: string]: any;
}
export interface FormDataFlags {
  [key: string]: string;
}

export interface FormInfo {
  /**
   * Flag necesario para que se dispare el journey con un dealer preseleccionado desde la url. En la primera carga omitimos la localización y el horario, pero en caso de que el
   * usuario vuelva hacia atrás el journey debe seguir siendo siempre el mismo.
   */
  firstMapLoad?: boolean;
  /**
   * Flag que se activa cuando se produce el primer dispatch al crear el formulario, para no mostrar el form hasta que no se inicie el mismo.
   */
  formStarted: boolean;
  /**
   * Flag que se activa al llegar al final del formulario. Es necesario en caso de edición de la cita para pasar del paso a editar a la confirmación de la cita sin volver a hacer todo el journey.
   */
  formEnded: boolean;
  /**
   * Flag en caso de que uno de los steps necesite mostrarse en fullscreen sin los botones de navegación.
   */
  formResponse?: any;

  /**
   * Flag para determinar el tema de color del form
   */
  formTheme?: 'main' | 'inverted';
  /**
   * Flag para determinar si el form se despliega en un layer o no
   */
  notALayer?: boolean;
  /**
   * Flag para determinar el tipo de front de un formulario: layer, encuesta o module
   */
  formType?: 'layer' | 'inPage' | 'module';

  /**
   * Flag para determinar si hay que alinear al centro, para forms formato full screen cuando solo hay una pantalla
   */
  alingItemsCenter?: boolean;
  /**
   * Flag que indica que el formulario ya ha terminado y se ha enviado.
   */
  formSubmitted: boolean;
  fullScreen: boolean;
  /**
   * Objeto con los parámetros necesarios para inicializar el mapa a partir de la búsqueda del suggest
   */
  geometry?: Geometry;
  /**
   * Flag para controlar si hay un error que impide que se pueda seguir avanzando y se tenga que cerrar el layer.
   */
  genericError: boolean;
  /**
   * En caso de que haya un paso final común se puede especificar para en el caso de que haya que volver hacia atrás, al usar el CTA de siguiente te rediriga a la última página.
   */
  initialZoom?: number;
  initialSteps?: Steps[];

  /**
   * Componente que se mostrará después de realizar el envío del formulario.
   */
  thankYouComponent: JSX.Element;
  lastStep?: Steps;
  multiSteps?: Steps[];
  multiStepScreenIndex?: number;
  /**
   * Flag para detectar si se está avanzando hacia adelante o hacia atrás (principalmente útil en los multisteps).
   */
  navigationMovingForward?: boolean;
  nextNumber?: number;
  nextStepCallback?: () => number;
  numberOfScreens: number;
  previousStepCallback?: () => void;
  screenIndex: number;
  /**
   * Flag que se activa en caso de que haya un problema con la DMS a la hora de grabar la cita.
   */
  sendDmsFailed?: boolean;
  sendFormCallback?: () => Promise<any>;
  showFinalScreen: boolean;
  /**
   * Flag para mostrar el calendario o no al volver hacia atrás en cita posventa.
   */
  showCalendarOnLoad?: boolean;
  step: Steps;
  steps: Steps[];
  stepsHistory: number[];
  /**
   * Flag para controlar en el mapa si el usuario ha seleccionado una concesión, para mostrar directamente el calendario.
   */
  suggestionIsDealer?: boolean;
  /**
   * Flag para impedir avanzar al usuario en caso de que seleccione un resultado en las islas canarias para poder redirigir.
   */
  suggestionIsInCanaryIslands?: boolean;
  /**
   * Flag que indica si la sugerencia seleccionada es una dirección o no, para mostrar el marker de geolocalización.
   */
  suggestionIsAddress?: boolean;
  /**
   * Desactivamos el tracking en algunos journeys
   */
  trackingIsDisabled?: boolean;
  /**
   * Flag para ocultar el legal que se muestra por defecto en la pantalla de confirmación.
   */
  hideLegal?: boolean;
  /**
   * Elemento para mostrar un legal custom en la pantalla de legales.
   */
  customLegal?: JSX.Element;
  triggerExitAnimation: boolean;
  userCenter?: Center;
  userIsAuthenticated?: boolean;
  userSuggestion?: Suggestion;
  userGeoLocation?: Suggestion;
  waitForNextStep: boolean;

  //ocultar el close component y el previous step en steps configurados especialmente
  deleteCloseAndPrevious?: boolean;
}

export interface Points {
  id?: number;
  type?: string;
  properties?: {
    parent_cluster_id?: number;
    cluster: boolean;
    concesionId?: string | number;
    dealer?: DealersData;
    installation?: Installation;
    cluster_id?: number;
    distanceFromCenter?: string;
    earliestSlot?: number;
    point_count?: number;
    selected?: boolean;
  };
  geometry?: {
    type?: string;
    coordinates?: [number, number];
  };
}

export interface Center {
  lat: number;
  lng: number;
}

export interface Steps {
  name?: string;
  component?: JSX.Element;
  fields?: JSX.Element;
  fullScreen?: boolean;
  multiStep?: boolean;
  multiStepLastScreen?: boolean;
  outputs?: string[];
  outputsText?: string[];
  outputsIsStars?: boolean;
  trueOrFalseQuestion?: boolean;
  screenIndex?: number;
  alternateTitle?: JSX.Element;
  title?: JSX.Element;
  isLastStep?: boolean;
  /**
   * Flag para controlar que se dispare el evento correcto de analítica en la primera carga del layer.
   */
  isFirstStep?: boolean;
  /**
   * Flag para ocultar si es necesario que se muestre el componente PreviousStep en el step.
   */
  hidePreviousStep?: boolean;
  /**
   * Flag para determinar si el step contiene un select con dropdown NO nativo
   */
  showOverflow?: boolean;
}

export interface MolecularResponse {
  content: {
    status: string;
    code: string;
    message: string;
    data: object;
  };
  params: {
    fields: {
      COD_LEAD_ID: string;
    };
    headers: object;
  };
  status: {
    code: number;
    result: string;
    route: string;
    time: number;
  };
  success: boolean;
}

export interface SalesGroupInfo {
  salesGroupId: string;
  salesGroupName: string;
}

export interface AppadapResponse extends MolecularResponse {
  contentNDPS: { status: string; code: string; message: string; data: object };
}
export interface DomainAttributesValues {
  name: DomainAttributesName;
  value: string;
}

type DomainAttributesName =
  | 'Id'
  | 'TXT_V_Installation_Data_Name__c'
  | 'FRML_TXT_codVentaProvincia__c'
  | 'TXT_V_Location__c'
  | 'TXT_V_Address__c'
  | 'TXT_V_Postal_Code__c'
  | 'TXT_V_Installation_Code__c'
  | 'MPCK_Instalation_Type__c'
  | 'TXT_V_KVPS_code__c'
  | 'TXT_Code_Concession__c'
  | 'Longitude__c'
  | 'Latitude__c';

export interface DomainAttributes {
  attributes: DomainAttributesValues[];
}

export interface DomainResponse {
  content: DomainAttributes[];
  params: {
    token: string;
    domain: string;
  };
  status: {
    code: string;
    result: string;
    route: string;
    time: number;
  };
}

export interface VicciResponse {
  expandedConfiguration: {
    salesgroupKey: string;
  };
}

export interface DealersData {
  id?: number | string;
  name?: string;
  kvps?: string;
  codInstalacion?: string;
  slots?: Slots;
  markerInfo?: {
    dealerInfo?: DealerInfo;
    svcLocation?: SvcLocation;
    id?: string;
  };
  dmsInfo?: DmsInfo;
  availabilityInfo?: null;
}
export interface Slots {
  morning: Slot;
  afternon: Slot;
  evening: Slot;
}
export interface Installation {
  Id: string;
  TXT_V_Installation_Data_Name__c: string;
  FRML_TXT_codVentaProvincia__c: string;
  TXT_V_Location__c: string;
  TXT_V_Address__c: string;
  TXT_V_Postal_Code__c: string;
  TXT_V_Installation_Code__c: string;
  MPCK_Instalation_Type__c: string;
  TXT_V_KVPS_code__c: string;
  TXT_Code_Concession__c: string;
  Longitude__c: string;
  Latitude__c: string;
}
export interface Slots {
  morning: Slot;
  afternon: Slot;
  evening: Slot;
}

export type SlotsTime = 'morning' | 'afternon' | 'evening';

export interface SvcLocation {
  latitude: number;
  longitude: number;
}

export interface Slot {
  from: number;
  to: number;
  replacementCarServiceAvailable: boolean;
  dayMoment: { code: string; name: string };
  assessorCode: string;
}

export interface DmsInfo {
  name?: string;
  phone?: string;
  email?: string;
  dmsAvailable: boolean;
  dealerId: {
    kvps: string;
    codInstalacion: string;
    brand: string;
  };
  replacementCarServiceAvailable: boolean;
  pickupCarServiceAvailabe: boolean;
  overhaulingServiceAvialable: boolean;
  assessors: Assessors[];
  reference: string;
}

export interface Assessors {
  code: string;
  name: string;
}
export interface DealerInfo {
  dealerId?: {
    kvps?: string;
    codInstalacion?: string;
    brand?: string;
  };
  name?: string;
  phone?: string;
  departmentCode?: string;
  contactName?: string;
  mail?: string;
  city?: string;
  province?: string;
  country?: string;
  address?: string;
  zipCode?: string;
  urls?: string[];
}

export interface Availability {
  status: {
    route: string;
    code: number;
    result: string;
    time: number;
  };
  content: {
    dealer: DmsInfo;
    calendar: Calendar[];
  };
}

export interface Calendar {
  date: string;
  slots: CalendarSlot[];
}

export interface Day {
  dayNumber: string;
  dayOfTheWeek: string;
  date: string;
  disabled: boolean;
  active: boolean;
}

export interface CalendarSlot {
  hour: string;
  assessor: string;
  from: number;
  to: number;
  replacementCarServiceAvailable: boolean;
  dayMoment: { code: string; name: string };
  assessorCode: string;
  GMT: string;
}

export interface Survey {
  dealer?: {
    markerInfo: {};
    dmsInfo: {};
  };
  tc?: string;
  deviceType: string;
  urlOrigin: string;
  url: string;
  formId: string;
  formVersion: string;
  deepLink?: string;
  budget?: string;
  waid?: string;
}

export interface Campaign {
  activo: number;
  salesforce: string;
  dfo: {
    ofertas: string;
  };
}

export interface CarlineBase {
  activo: boolean;
  activoMatch: boolean;
  activoMeasurements: boolean;
  activoOffers: boolean;
  campaign: Campaign;
  capacidadMaletero: number[];
  categorias: Object;
  code: number;
  id: number;
  legalConsumos: string;
  medidas: Object;
  name: string;
  nuevo: boolean;
  precioMinimo?: number;
  precioMinimoModalidad?: string;
  precioMinimoText?: string;
  showroomURL: string;
  text: string;
  title: string;
  plazas: number;
  formularios: {
    testDrive: FormulariosFlagValue;
    presupuesto: FormulariosFlagValue;
    masInformacion: FormulariosFlagValue;
    prospect: FormulariosFlagValue;
  };
}

export interface CarlinesWithSalesgroups extends CarlineBase {
  salesGroups?: Salesgroup[];
}

export interface Salesgroup {
  id: number;
  code: number;
  name: string;
  year: number;
}

export interface Offer {
  anchor: string;
  activo: number;
  carlineCode: number;
  carlineName: string;
  carlineText: string;
  carlineTitle: string;
  carlineNuevo: number;
  colores: {
    exterior: ExteriorColors;
  };
  cuota: number;
  cuotaInfo: string;
  cuotaText: string;
  cuotaTipo: string;
  cuotaFinal: string;
  dco: number;
  descripcion: string;
  equipamientos?: OfferEquipments;
  entrada: string;
  financiacionName: string;
  financiacionText: string;
  id: number;
  infoCuota: string;
  legal: string;
  marketingCode: string;
  mediaAssets: OfferMediaAssets;
  modalidad: string;
  motorizacion: string;
  motor: {
    combustible: string;
    dsg: number;
    fourMotion: number;
    tiptronic: number;
    cambio: string;
  };
  myRenting: boolean;
  ncuotas: number;
  next: boolean;
  ourRenting: boolean;
  precio: number;
  precioText: string;
  prioridad: number;
  promociones: Promociones[];
  tin: string;
  tae: string;
}

export interface Promociones {
  name: string;
  text: string;
  descripcion: string;
}

export interface ExteriorColors {
  nombre: string;
  codigo: string;
  hexadecimal: string;
  invertido: boolean;
  atrevido: boolean;
  elegante: boolean;
}
export interface OfferEquipments {
  serie: OfferEquipment[];
  promocional: OfferEquipment[];
  destacados: OfferEquipment[];
}
export interface OfferEquipment {
  equipamiento: {
    titulo: string;
    descripcion: string;
    icono: string;
  };
}

export interface OfferMediaAssets {
  CarSideview: string;
  'CarStage3-4': string;
}

export type FormularioFlagType = 'testDrive' | 'presupuesto' | 'masInformacion' | 'prospect';
export type FormulariosFlagValue = 0 | 1;

export interface Geometry {
  lat: number;
  lng: number;
  bounds?: google.maps.LatLngBounds;
  viewport?: google.maps.LatLngBounds;
}

export type SelectedTab = 'ubicacion' | 'horario';

export interface IdLead {
  ID?: string;
  attributes?: string;
  Id?: string;
  OwnerId?: string;
  IsDeleted?: string;
  Name?: string;
  CreatedDate?: string;
  CreatedById?: string;
  LastModifiedDate?: string;
  LastModifiedById?: string;
  SystemModstamp?: string;
  LastViewedDate?: string;
  LastReferencedDate?: string;
  Brand__c?: string;
  DESC_Dealer_Code__c?: string;
  DESC_Model__c?: string;
  ID_Person_DIM_IMP__c?: string;
  IS_Prospect__c?: string;
  Is_AfterSales__c?: string;
  Is_Company__c?: string;
  Is_lead__c?: string;
  LSSI__c?: string;
  LastCreatedDate__c?: string;
  TXT_Email__c?: string;
  TXT_Postal_Code__c?: string;
  TXT_Province__c?: string;
  Token__c?: string;
  Total__c?: string;
  Total_last_1_year__c?: string;
  Total_last_2_year__c?: string;
  Total_last_3_year__c?: string;
  Total_last_4_year__c?: string;
  VN_VO__c?: string;
  LKP_PersonAccount__c?: string;
  External_Id_Climapro__c?: string;
  NUM_COD_INTEREST_CARLINE__c?: string;
  TXT_DESC_DEALER_CODE__c?: string;
  IsQualified__c?: string;
}

export interface IdPerson {
  Id?: string;
  IsDeleted?: string;
  Name?: string;
  TXT_2_Surname__c?: string;
  LastName?: string;
  FirstName?: string;
  Salutation?: string;
  RecordTypeId?: string;
  BillingAddress?: string;
  ShippingAddress?: string;
  PhotoUrl?: string;
  OwnerId?: string;
  CreatedDate?: string;
  CreatedById?: string;
  LastModifiedDate?: string;
  LastModifiedById?: string;
  SystemModstamp?: string;
  LastViewedDate?: string;
  LastReferencedDate?: string;
  IsPartner?: string;
  PersonContactId?: string;
  IsPersonAccount?: string;
  PersonMailingAddress?: string;
  PersonOtherAddress?: string;
  PersonHasOptedOutOfEmail?: string;
  PersonHasOptedOutOfFax?: string;
  PersonDoNotCall?: string;
  Audi__c?: string;
  Instalacion__c?: string;
  Skoda__c?: string;
  Volkswagen__c?: string;
  COD_Gender__c?: string;
  ID_Person_DIM_IMP__c?: string;
  LCV__c?: string;
  LSSI__c?: string;
  NUM_Phone_mobile__c?: string;
  Robinson__c?: string;
  TXT_Country__c?: string;
  TXT_Email__c?: string;
  TXT_Location__c?: string;
  TXT_NIF_CIF__c?: string;
  TXT_Postal_Code__c?: string;
  TXT_Province__c?: string;
  TXT_natural_legal_person__c?: string;
  FORM_Full_Address__c?: string;
}
export interface IdVehicle {
  ID?: string;
  attributes?: string;
  Id?: string;
  OwnerId?: string;
  IsDeleted?: string;
  Name?: string;
  CreatedDate?: string;
  CreatedById?: string;
  LastModifiedDate?: string;
  LastModifiedById?: string;
  SystemModstamp?: string;
  LastViewedDate?: string;
  LastReferencedDate?: string;
  Affected_EA_189__c?: string;
  COD_comision__c?: string;
  CarNet__c?: string;
  TMAIMG__c?: string;
  Warranty_extension__c?: string;
  Included_mainetance__c?: string;
  DESC_car_model_salesgroup__c?: string;
  PKL_order_brand__c?: string;
  Frame__c?: string;
  Fuel__c?: string;
  Gear_type__c?: string;
  Installed_PR_packages__c?: string;
  KM_cobertura_garantia__c?: string;
  Assistance_years__c?: string;
  DESC_Model__c?: string;
  Registration__c?: string;
  Repaired_EA_189__c?: string;
  TrimLevel_Model__c?: string;
  VW_Connect__c?: string;
  Vehicle_age_yearly_reg__c?: string;
  Audi_Connect__c?: string;
  Car_power__c?: string;
  Carline_Code__c?: string;
  DAT_registration__c?: string;
  DESC_model_group__c?: string;
  IDClimapro__c?: string;
  Long_full_drive__c?: string;
  PersonAccount__c?: string;
  Salesgroup_Code__c?: string;
  Vehicle_age_monthly_reg__c?: string;
  years_warranty_coverage__c?: string;
  years_warranty_extension__c?: string;
}

export interface IdOrder {
  ID?: string;
  attributes?: string;
  Id?: string;
  OwnerId?: string;
  IsDeleted?: string;
  Name?: string;
  CreatedDate?: string;
  CreatedById?: string;
  LastModifiedDate?: string;
  LastModifiedById?: string;
  SystemModstamp?: string;
  LastViewedDate?: string;
  LastReferencedDate?: string;
  CHK_excessive_waiting_Audi__c?: string;
  CHK_excessive_waiting_Audi_theoric__c?: string;
  CHK_excessive_waiting_LCV__c?: string;
  CHK_excessive_waiting_Skoda__c?: string;
  CHK_excessive_waiting_VW__c?: string;
  COD_Carline__c?: string;
  Instalacion_Audi__c?: string;
  Instalacion_LCV__c?: string;
  Instalacion_SK__c?: string;
  COD_comision__c?: string;
  COD_sales_type__c?: string;
  COD_salesgroup__c?: string;
  Customer_change_order__c?: string;
  VipEnviado__c?: string;
  DemoraTipo__c?: string;
  ConfirmacionRegaloEnviado__c?: string;
  EmailFollowUpEnviado__c?: string;
  CHK_contactable_universe__c?: string;
  Contactable_universe_vehicle__c?: string;
  DESC_Model__c?: string;
  DESC_Sales_VN_Type__c?: string;
  DESC_TrimLevel__c?: string;
  DESC_car_model_salesgroup__c?: string;
  DESC_excessive_waiting_Audi__c?: string;
  DESC_model_group__c?: string;
  EmailBienvenidaEnviado__c?: string;
  EmailDemoraEnviado__c?: string;
  EmailEstadoEnviado__c?: string;
  Factory__c?: string;
  Not_lineal_order__c?: string;
  Order_frame_FK__c?: string;
  PKL_order_VN_VO__c?: string;
  PKL_order_brand__c?: string;
  SLI_State__c?: string;
  Vehicle_age_monthly_ord__c?: string;
  Vehicle_age_yearly_ord__c?: string;
  COD_Customer_renting__c?: string;
  COD_Customer_type__c?: string;
  COD_Driver__c?: string;
  COD_Vehicle_climapro__c?: string;
  COD_owner_climapro__c?: string;
  Concesion__c?: string;
  Customer_type__c?: string;
  DAT_Customers_order__c?: string;
  DAT_Order__c?: string;
  DAT_delivery__c?: string;
  DAT_order_state_10__c?: string;
  DAT_order_state_20__c?: string;
  DAT_order_state_30__c?: string;
  DAT_order_state_40__c?: string;
  DAT_order_state_50__c?: string;
  DAT_order_state_60__c?: string;
  DAT_order_state_70__c?: string;
  DAT_order_state_80__c?: string;
  IDClimapro__c?: string;
  Instalacion__c?: string;
}

export interface IdDealer {
  ID: string;
  attributes: string;
  Id: string;
  IsDeleted: string;
  MasterRecordId: string;
  Name: string;
  LastName: string;
  FirstName: string;
  Salutation: string;
  MiddleName: string;
  Suffix: string;
  Type: string;
  RecordTypeId: string;
  ParentId: string;
  BillingStreet: string;
  BillingCity: string;
  BillingState: string;
  BillingPostalCode: string;
  BillingCountry: string;
  BillingLatitude: string;
  BillingLongitude: string;
  BillingGeocodeAccuracy: string;
  BillingAddress: string;
  ShippingStreet: string;
  ShippingCity: string;
  ShippingState: string;
  ShippingPostalCode: string;
  ShippingCountry: string;
  ShippingLatitude: string;
  ShippingLongitude: string;
  ShippingGeocodeAccuracy: string;
  ShippingAddress: string;
  Phone: string;
  Fax: string;
  AccountNumber: string;
  Website: string;
  PhotoUrl: string;
  Sic: string;
  Industry: string;
  AnnualRevenue: string;
  NumberOfEmployees: string;
  Ownership: string;
  TickerSymbol: string;
  Description: string;
  Rating: string;
  Site: string;
  OwnerId: string;
  CreatedDate: string;
  CreatedById: string;
  LastModifiedDate: string;
  LastModifiedById: string;
  SystemModstamp: string;
  LastActivityDate: string;
  LastViewedDate: string;
  LastReferencedDate: string;
  IsPartner: string;
  PersonContactId: string;
  IsPersonAccount: string;
  PersonMailingStreet: string;
  PersonMailingCity: string;
  PersonMailingState: string;
  PersonMailingPostalCode: string;
  PersonMailingCountry: string;
  PersonMailingLatitude: string;
  PersonMailingLongitude: string;
  PersonMailingGeocodeAccuracy: string;
  PersonMailingAddress: string;
  PersonMobilePhone: string;
  PersonEmail: string;
  PersonTitle: string;
  PersonDepartment: string;
  PersonHasOptedOutOfEmail: string;
  PersonLastCURequestDate: string;
  PersonLastCUUpdateDate: string;
  PersonEmailBouncedReason: string;
  PersonEmailBouncedDate: string;
  Jigsaw: string;
  JigsawCompanyId: string;
  AccountSource: string;
  SicDesc: string;
  SalesforceCustomer: string;
  Active__c: string;
  Audi__c: string;
  Customer_Priority__c: string;
  Instalacion__c: string;
  NumberofLocations__c: string;
  SLAExpirationDate__c: string;
  SLASerialNumber__c: string;
  SLA__c: string;
  Skoda__c: string;
  UpsellOpportunity__c: string;
  Volkswagen__c: string;
  Dealer_Code_Vaesa__c: string;
  COD_Gender__c: string;
  ID_Person_DIM_IMP__c: string;
  LCV__c: string;
  LSSI__c: string;
  NUM_Phone_mobile__c: string;
  NUM_landline__c: string;
  PCK_Brand__c: string;
  Robinson__c: string;
  TXT_Country__c: string;
  TXT_Email__c: string;
  TXT_Location__c: string;
  TXT_NIF_CIF__c: string;
  TXT_Postal_Code__c: string;
  TXT_Province__c: string;
  TXT_natural_legal_person__c: string;
  Address_extra__c: string;
  Address_name__c: string;
  Address_number__c: string;
  Address_type__c: string;
  FORM_Full_Address__c: string;
  TXT_Code_Concession__c: string;
  TXT_Installation_Code__c: string;
  TXT_V_Code_Concession__c: string;
  TXT_V_Installation_Code__c: string;
  CHK_Lead_VN_Audi__c: string;
  CHK_Lead_VN_LCV__c: string;
  CHK_Lead_VN_SK__c: string;
  CHK_Lead_VN_VW__c: string;
  CHK_Lead_VO_Audi__c: string;
  CHK_Lead_VO_LCV__c: string;
  CHK_Lead_VO_SK__c: string;
  CHK_Lead_VO_VW__c: string;
  CHK_Prospect_VN_Audi__c: string;
  CHK_Prospect_VN_LCV__c: string;
  CHK_Prospect_VN_SK__c: string;
  CHK_Prospect_VN_VW__c: string;
  CHK_Prospect_VO_Audi__c: string;
  CHK_Prospect_VO_LCV__c: string;
  CHK_Prospect_VO_SK__c: string;
  CHK_Prospect_VO_VW__c: string;
  DAT_last_received_com_LCV__c: string;
  DAT_last_received_com_SK__c: string;
  DAT_last_received_com_VW__c: string;
  DAT_last_received_com__c: string;
  DAT_lead_vn_LCV__c: string;
  DAT_lead_vn_SK__c: string;
  DAT_lead_vn_VW__c: string;
  DAT_lead_vn__c: string;
  DAT_lead_vo_LCV__c: string;
  DAT_lead_vo_SK__c: string;
  DAT_lead_vo_VW__c: string;
  DAT_lead_vo__c: string;
  Desired_model_1_LCV__c: string;
  Desired_model_1_Skoda__c: string;
  Desired_model_1_VW__c: string;
  Desired_model_1__c: string;
  Desired_model_2_LCV__c: string;
  Desired_model_2_Skoda__c: string;
  Desired_model_2_VW__c: string;
  Desired_model_2__c: string;
  Last_comunication_campaign_LCV__c: string;
  Last_comunication_campaign_SK__c: string;
  Last_comunication_campaign_VW__c: string;
  Last_comunication_campaign__c: string;
  N_hits_last_14_days_LCV__c: string;
  N_hits_last_14_days_SK__c: string;
  N_hits_last_14_days_VW__c: string;
  N_hits_last_14_days__c: string;
  N_hits_last_21_days_LCV__c: string;
  N_hits_last_21_days_SK__c: string;
  N_hits_last_21_days_VW__c: string;
  N_hits_last_21_days__c: string;
  N_hits_last_30_days_LCV__c: string;
  N_hits_last_30_days_SK__c: string;
  N_hits_last_30_days_VW__c: string;
  N_hits_last_30_days__c: string;
  N_hits_last_7_days_LCV__c: string;
  N_hits_last_7_days_SK__c: string;
  N_hits_last_7_days_VW__c: string;
  N_hits_last_7_days__c: string;
  N_hits_last_90_days_LCV__c: string;
  N_hits_last_90_days_SK__c: string;
  N_hits_last_90_days_VW__c: string;
  N_hits_last_90_days__c: string;
  N_hits_last_year_LCV__c: string;
  N_hits_last_year_SK__c: string;
  N_hits_last_year_VW__c: string;
  N_hits_last_year__c: string;
  Pissed_off_client_LCV__c: string;
  Pissed_off_client_Skoda__c: string;
  Pissed_off_client_VW__c: string;
  Pissed_off_client__c: string;
  driver_is_official_dealer_LCV__c: string;
  driver_is_official_dealer_SK__c: string;
  driver_is_official_dealer_VW__c: string;
  driver_is_official_dealer__c: string;
  driver_vn_act_SK__c: string;
  driver_vn_act_LCV__c: string;
  driver_vn_act_VW__c: string;
  driver_vn_act__c: string;
  driver_vn_nact_LCV__c: string;
  driver_vn_nact_Sk__c: string;
  driver_vn_nact_VW__c: string;
  driver_vn_nact__c: string;
  driver_vo_act_LCV__c: string;
  driver_vo_act_SK__c: string;
  driver_vo_act_VW__c: string;
  driver_vo_act__c: string;
  driver_vo_nact_LCV__c: string;
  driver_vo_nact_SK__c: string;
  driver_vo_nact_VW__c: string;
  driver_vo_nact__c: string;
  owner_is_offical_dealer_LCV__c: string;
  owner_is_offical_dealer_SK__c: string;
  owner_is_offical_dealer_VW__c: string;
  owner_is_offical_dealer__c: string;
  owner_vn_act_LCV__c: string;
  owner_vn_act_SK__c: string;
  owner_vn_act_VW__c: string;
  owner_vn_act__c: string;
  owner_vn_nact_LCV__c: string;
  owner_vn_nact_SK__c: string;
  owner_vn_nact_VW__c: string;
  owner_vn_nact__c: string;
  owner_vo_act_LCV__c: string;
  owner_vo_act_SK__c: string;
  owner_vo_act_VW__c: string;
  owner_vo_act__c: string;
  owner_vo_nact_LCV__c: string;
  owner_vo_nact_SK__c: string;
  owner_vo_nact_VW__c: string;
  owner_vo_nact__c: string;
  COD_province__c: string;
  Manual_marker__c: string;
  TXT_V_Address__c: string;
  Afected_EA_189__c: string;
  DAT_prospect_vn_LCV__c: string;
  DAT_prospect_vn_SK__c: string;
  DAT_prospect_vn_VW__c: string;
  DAT_prospect_vn__c: string;
  DAT_prospect_vo_LCV__c: string;
  DAT_prospect_vo_SK__c: string;
  DAT_prospect_vo_VW__c: string;
  DAT_prospect_vo__c: string;
  Legal_risk_EA_189__c: string;
  Age__c: string;
  CHK_Current_Afected_EA_189__c: string;
  Document_type__c: string;
  Repaired_EA_189__c: string;
  LKP_After_Sales_manager__c: string;
  LKP_Sales_manager__c: string;
  Dealer_Code_Imaweb__c: string;
  ID_Climapro_Installation_Audi__c: string;
  ID_Climapro_Installation_LCV__c: string;
  ID_Climapro_Installation_SK__c: string;
  ID_Climapro_Installation_VW__c: string;
  Latitude__c: string;
  Longitude__c: string;
  NUM_V_Phone__c: string;
  Preferred_Installation_Aftersale_Audi__c: string;
  Preferred_Installation_Aftersale_LCV__c: string;
  Preferred_Installation_Aftersale_SK__c: string;
  Preferred_Installation_Aftersale_VW__c: string;
  Preferred_Installation_Sale_Audi__c: string;
  Preferred_Installation_Sale_LCV__c: string;
  Preferred_Installation_Sale_SK__c: string;
  Preferred_Installation_Sale_VW__c: string;
  TXT_KVPS_code__c: string;
  TXT_PV_Address__c: string;
  TXT_V_KVPS_code__c: string;
  TXT_V_Postal_Code__c: string;
  Cod_installation_Imaweb__c: string;
  FLAG_Baja__c: string;
  COD_Brand_Imaweb__c: string;
  NORM_Address_name__c: string;
  NORM_Address_number__c: string;
  NORM_Location__c: string;
  NORM_Postal_Code__c: string;
  TXT_Business_Group_CIF__c: string;
  TXT_Description_Concession__c: string;
  TXT_Email_Contact_Concession__c: string;
  TXT_Type_Concession__c: string;
  ID_Climapro_Concession__c: string;
  TXT_V_Email__c: string;
  Latitude_V__c: string;
  Longitude_V__c: string;
  TXT_V_Location__c: string;
  COD_V_province__c: string;
  Cod_V_installation_Imaweb__c: string;
  FLAG_V_Baja__c: string;
  Dealer_V_Code_Vaesa__c: string;
  ID_V_Climapro_Installation_Audi__c: string;
  ID_V_Climapro_Installation_LCV__c: string;
  ID_V_Climapro_Installation_SK__c: string;
  ID_V_Climapro_Installation_VW__c: string;
  Dealer_V_Code_Imaweb__c: string;
  TXT_Area_Manager_V__c: string;
  TXT_Email_Zone_Manager__c: string;
  External_ID_Otros__c: string;
  TXT_Activity__c: string;
  TXT_Last_Name_1_Manager__c: string;
  TXT_Surname_2_Manager__c: string;
  TXT_Area_code__c: string;
  TXT_Description_Installation__c: string;
  TXT_prior_appointment__c: string;
  TXT_Email_After_Sales_PV__c: string;
  TXT_Email_Manager__c: string;
  TXT_V_Email_more_info__c: string;
  TXT_Email_Sender__c: string;
  DATE_Home__c: string;
  DATE_Date_Last_Updated__c: string;
  CHK_Replacement_fleet__c: string;
  TXT_V_Installation_Data_Name__c: string;
  TXT_Installation_Data_Name__c: string;
  TXT_Manager_Name__c: string;
  TXT_Observations_Schedule__c: string;
  TXT_V_Observations_Schedule__c: string;
  CHK_V_Parking__c: string;
  TXT_business_name__c: string;
  TXT_V_business_name__c: string;
  Adblue_price__c: string;
  Anyo_matriculacion_otro_vehiculo__c: string;
  CHK_Audi_CAM__c: string;
  CHK_Audi_Lounge__c: string;
  CHK_Audi_Sport_Exclusive_Service__c: string;
  CHK_Audi_Sport__c: string;
  CHK_California_roof_repairing__c: string;
  CHK_Children_zone__c: string;
  CHK_Client_Shutle__c: string;
  CHK_Compressed_Natural_Gas_Charger__c: string;
  CHK_Crafter_Elevator__c: string;
  CHK_DRM__c: string;
  CHK_Delivery_Service__c: string;
  CHK_Do_Not_Contact_AU__c: string;
  CHK_Do_Not_Contact__c: string;
  CHK_Dont_send_Com__c: string;
  CHK_Elite_Body__c: string;
  CHK_Email_OFF__c: string;
  CHK_Exclusive_Service_VW__c: string;
  CHK_Exclusive_Service__c: string;
  CHK_Fast_Service__c: string;
  CHK_Free_Wifi__c: string;
  CHK_HVT_repairs__c: string;
  CHK_Handicapped__c: string;
  CHK_Integral_Body_Program__c: string;
  CHK_Is_Closed_Days__c: string;
  CHK_Oficial_Audi_Prestige_Service__c: string;
  CHK_PCI__c: string;
  CHK_Parking__c: string;
  CHK_SUM_Diff_Schedule_Vehicle_Recep__c: string;
  CHK_Service_Plus_Installation__c: string;
  CHK_TLMK__c: string;
  CHK_V_Children_zone__c: string;
  CHK_V_Exclusive_Service_VW__c: string;
  CHK_V_Free_Wifi__c: string;
  CHK_V_Handicapped__c: string;
  CHK_V_Waiting_zone__c: string;
  CHK_Waiting_zone__c: string;
  CHK_Working_Zone_Waiting_Clients__c: string;
  CHK_e_Hibrids__c: string;
  CHK_e_dealers__c: string;
  CHK_e_loaders__c: string;
  CampaignID__c: string;
  Contactable_after_sales__c: string;
  Contactable_sales__c: string;
  Customer_type__c: string;
  DATE_Date_Photo__c: string;
  DATE_End_Date_SUM__c: string;
  DATE_End_Date_Valid__c: string;
  DATE_Entry_Date__c: string;
  DATE_Ini_Date_SUM__c: string;
  DATE_Termination_Date__c: string;
  DATE_View_End_Period__c: string;
  DATE_View_Start_Period__c: string;
  DAT_Customer_registration_SLI__c: string;
  DAT_last_vehicle__c: string;
  Email_Business_Group__c: string;
  Es_Autonomo_VN__c: string;
  Es_Gran_empresa_propiedad_VN__c: string;
  Es_Pymes_propiedad_VN__c: string;
  Es_Pymes_renting_VN__c: string;
  Es_RaC_VN__c: string;
  Es_VO_no_particular__c: string;
  Es_gran_empresa_renting_VN__c: string;
  Es_indefinido_VN__c: string;
  Es_no_identificado_VN__c: string;
  Es_particular_VN__c: string;
  Es_resto_VN__c: string;
  Excesive_waiting__c: string;
  FRML_TXT_DPV__c: string;
  FRML_TXT_Image_Sign__c: string;
  Family_unit__c: string;
  FormID__c: string;
  Home_another_vehicle_brand__c: string;
  ID_External_ID__c: string;
  ID_Vehicle_EID__c: string;
  Id_Instalacion__c: string;
  Id_Propietario_PV_AU__c: string;
  Id_Propietario_PV_LCV__c: string;
  Id_Propietario_PV_SK__c: string;
  Id_Propietario_PV_VW__c: string;
  Id_Propietario_V_AU__c: string;
  Id_Propietario_V_LCV__c: string;
  Id_Propietario_V_SK__c: string;
  Id_Propietario_V_VW__c: string;
  Informat_cession_aceptance__c: string;
  Lead_vn_act__c: string;
  Lead_vn_nact__c: string;
  Lead_vo_act__c: string;
  Lead_vo_nact__c: string;
  Multivehicle__c: string;
  NUM_Bodywork_Price_Hour__c: string;
  NUM_Business_Group_Id__c: string;
  NUM_Fax_Business_Group__c: string;
  NUM_Id_Operation__c: string;
  NUM_Mechanical_Price_Hour__c: string;
  NUM_Number_Photo__c: string;
  NUM_Oil_Liter_Price__c: string;
  NUM_Phone_Business_Group__c: string;
  NUM_Substitution_vehicle__c: string;
  NUM_Work_Price__c: string;
  NUM_chassis_number__c: string;
  PCK_SUM_TIME_FRI_AFT_END__c: string;
  PCK_SUM_TIME_FRI_AFT_STA__c: string;
  PCK_SUM_TIME_FRI_MOR_END__c: string;
  PCK_SUM_TIME_FRI_MOR_STA__c: string;
  PCK_SUM_TIME_MON_THUR_AFT_END__c: string;
  PCK_SUM_TIME_MON_THUR_AFT_STA__c: string;
  PCK_SUM_TIME_MON_THUR_MOR_END__c: string;
  PCK_SUM_TIME_MON_THUR_MOR_STA__c: string;
  PCK_SUM_TIME_SAT_AFT_END__c: string;
  PCK_SUM_TIME_SAT_AFT_STA__c: string;
  PCK_SUM_TIME_SAT_MOR_END__c: string;
  PCK_SUM_TIME_SAT_MOR_STA__c: string;
  PCK_SUM_TIME_SUN_AFT_END__c: string;
  PCK_SUM_TIME_SUN_AFT_STA__c: string;
  PCK_SUM_TIME_SUN_MOR_END__c: string;
  PCK_SUM_TIME_SUN_MOR_STA__c: string;
  PCK_Social_Network__c: string;
  PCK_V_SUM_TIME_FRI_AFT_END__c: string;
  PCK_V_SUM_TIME_FRI_AFT_STA__c: string;
  PCK_V_SUM_TIME_FRI_MOR_END__c: string;
  PCK_V_SUM_TIME_FRI_MOR_STA__c: string;
  PCK_V_SUM_TIME_MON_THUR_AFT_END__c: string;
  PCK_V_SUM_TIME_MON_THUR_AFT_STA__c: string;
  PCK_V_SUM_TIME_MON_THUR_MOR_END__c: string;
  PCK_V_SUM_TIME_MON_THUR_MOR_STA__c: string;
  PCK_V_SUM_TIME_SAT_AFT_END__c: string;
  PCK_V_SUM_TIME_SAT_AFT_STA__c: string;
  PCK_V_SUM_TIME_SAT_MOR_END__c: string;
  PCK_V_SUM_TIME_SAT_MOR_STA__c: string;
  PCK_V_SUM_TIME_SUN_AFT_END__c: string;
  PCK_V_SUM_TIME_SUN_AFT_STA__c: string;
  PCK_V_SUM_TIME_SUN_MOR_END__c: string;
  PCK_V_SUM_TIME_SUN_MOR_STA__c: string;
  PCK_V_Social_Network__c: string;
  PCK_V_WIN_TIME_FRI_AFT_END__c: string;
  PCK_V_WIN_TIME_FRI_AFT_STA__c: string;
  PCK_V_WIN_TIME_FRI_MOR_END__c: string;
  PCK_V_WIN_TIME_FRI_MOR_STA__c: string;
  PCK_V_WIN_TIME_MON_THUR_AFT_END__c: string;
  PCK_V_WIN_TIME_MON_THUR_AFT_STA__c: string;
  PCK_V_WIN_TIME_MON_THUR_MOR_END__c: string;
  PCK_V_WIN_TIME_MON_THUR_MOR_STA__c: string;
  PCK_V_WIN_TIME_SAT_AFT_END__c: string;
  PCK_V_WIN_TIME_SAT_AFT_STA__c: string;
  PCK_V_WIN_TIME_SAT_MOR_END__c: string;
  PCK_V_WIN_TIME_SAT_MOR_STA__c: string;
  PCK_V_WIN_TIME_SUN_AFT_END__c: string;
  PCK_V_WIN_TIME_SUN_AFT_STA__c: string;
  PCK_V_WIN_TIME_SUN_MOR_END__c: string;
  PCK_V_WIN_TIME_SUN_MOR_STA__c: string;
  PCK_WIN_TIME_FRI_AFT_END__c: string;
  PCK_WIN_TIME_FRI_AFT_STA__c: string;
  PCK_WIN_TIME_FRI_MOR_END__c: string;
  PCK_WIN_TIME_FRI_MOR_STA__c: string;
  PCK_WIN_TIME_MON_THUR_AFT_END__c: string;
  PCK_WIN_TIME_MON_THUR_AFT_STA__c: string;
  PCK_WIN_TIME_MON_THUR_MOR_END__c: string;
  PCK_WIN_TIME_MON_THUR_MOR_STA__c: string;
  PCK_WIN_TIME_SAT_AFT_END__c: string;
  PCK_WIN_TIME_SAT_AFT_STA__c: string;
  PCK_WIN_TIME_SAT_MOR_END__c: string;
  PCK_WIN_TIME_SAT_MOR_STA__c: string;
  PCK_WIN_TIME_SUN_AFT_END__c: string;
  PCK_WIN_TIME_SUN_AFT_STA__c: string;
  PCK_WIN_TIME_SUN_MOR_END__c: string;
  PCK_WIN_TIME_SUN_MOR_STA__c: string;
  PKL_Exclusion_Motive_PV_AU__c: string;
  PKL_Exclusion_Motive_PersonAccount__c: string;
  Profession__c: string;
  Standard_contactable_universe_LCV__c: string;
  Standard_contactable_universe_Skoda__c: string;
  Standard_contactable_universe_VW__c: string;
  Standard_contactable_universe__c: string;
  TXT_Accept_Bases__c: string;
  TXT_Area_Manager_PV__c: string;
  TXT_Business_Group_Name__c: string;
  TXT_BussinesGroup__c: string;
  TXT_Comments_Exclusion__c: string;
  TXT_Context_ID__c: string;
  TXT_Contract_brand__c: string;
  TXT_DPV_Name__c: string;
  TXT_DPV_gender__c: string;
  TXT_DPV_treatment__c: string;
  TXT_Description_Validity__c: string;
  TXT_Email_Area_Manager__c: string;
  TXT_Email_CCDPV__c: string;
  TXT_Email_DPV__c: string;
  TXT_Email_DV__c: string;
  TXT_Email_Online_Appointment_PV__c: string;
  TXT_Email_Online_Appointment_V__c: string;
  TXT_Email_Zone_Manager_V__c: string;
  TXT_Facebook__c: string;
  TXT_Instagram__c: string;
  TXT_Last_Name_1_Area_Manager_PV__c: string;
  TXT_Last_Name_1_DPV__c: string;
  TXT_Last_Name_1_V__c: string;
  TXT_Last_name_2_DPV__c: string;
  TXT_Last_name_2_V__c: string;
  TXT_Manager_Name_PV__c: string;
  TXT_Name__c: string;
  TXT_Observations__c: string;
  TXT_Official_Web_PV__c: string;
  TXT_Official_Web_V__c: string;
  TXT_Position__c: string;
  TXT_Profile__c: string;
  TXT_Surname_2_Area_Manager_PV__c: string;
  TXT_Tradename_BusinessGroup__c: string;
  TXT_Tradename__c: string;
  TXT_Twitter__c: string;
  TXT_V_Facebook__c: string;
  TXT_V_Instagram__c: string;
  TXT_V_Name__c: string;
  TXT_V_Tradename__c: string;
  TXT_V_Twitter__c: string;
  TXT_V_Web__c: string;
  TXT_V_gender__c: string;
  TXT_Web__c: string;
  TXT_number_plate__c: string;
  URL_Business_Group_Website__c: string;
  Unsuscribed__c: string;
  Use_type__c: string;
  cond_is_official_dealer_LCV__c: string;
  cond_is_official_dealer_SK__c: string;
  cond_is_official_dealer_VW__c: string;
  cond_is_official_dealer__c: string;
  cond_vn_act_SK__c: string;
  prospect_vn_act__c: string;
  prospect_vn_nact__c: string;
  prospect_vo_act__c: string;
  prospect_vo_nact__c: string;
  et4ae5__HasOptedOutOfMobile__pc: string;
  et4ae5__Mobile_Country_Code__pc: string;
  IDClimapro__pc: string;
}

export interface modelCar {
  id: number;
  name: string;
  title: string;
  brand: string;
  model: string;
}

export type PassengersTypes = [number, number];
export type FuelTypes = 'Combustible' | 'EléctricoHibrido';
export type LuggageReturn = 'unaMaleta' | 'escapada' | 'unaSemana' | 'unMes' | 'mascota' | 'bici' | 'surf' | 'snow';
export type UsageReturn = 'urban' | 'interurban' | 'long' | 'allterrain';
export type HighlightsReturn =
  | 'atrevido'
  | 'comodo'
  | 'deportivo'
  | 'sostenible'
  | 'elegante'
  | 'seguro'
  | 'sonido'
  | 'tecnologico';

// Necesario para el envío de los formularios de Match
export interface MatchSelectedOptions {
  budget: number;
  passengers: [number, number];
  luggage: number[];
  usage: number[];
  highlights: number[];
  fuel: FuelTypes;
}

export interface ArgumentativeBlock {
  key: string;
  icon?: JSX.Element;
  title?: string;
  boldTitle?: string;
  bodycopy?: string;
  titleRevers?: boolean;
}
export interface Color {
  'id': number;
  'title': string;
  'code': string;
  'hex': string;
  'inverted': number;
  'interior': number;
  'elegante': number;
  'atrevido': number;
}

export interface CarlineWithOffers extends Preconfiguration {
  ofertas: Offer[];
}